/**
 * decorationFamilySelectors -- the read layer over the decoration
 * FAMILY payload (`GET /api/artworks/<id>/family`).
 *
 * Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
 * Handoff: planning/design-handoffs/handoff_2026-08-23/
 *          2026-08-23-decoration-family-model/
 * ADR 0067 + its ThreadFlow amendment (adr/0067-AMENDMENT-threadflow.md).
 *
 * Why this module exists at all
 * =============================
 *
 * ADR 0067's load-bearing rule is that **nothing may read broader than
 * the size it sits on**. The backend already enforces that
 * structurally: `services/decoration_family_view.py` puts approval
 * values ONLY inside `families[].sizes[].approvals`, with no
 * design-level, version-level or family-level approval key for a
 * caller to read.
 *
 * The interface has to keep the same promise, and the design pass's
 * own prototype shows how that promise gets broken. `dm-parts.jsx:31`
 * builds the per-size pills from `apprList(d, v)`; `dm-parts.jsx:186`
 * then re-derives the accordion by open-coding `v.appr[client]`. Two
 * readers of one fact, agreeing only by the author's care --
 * `docs/design-philosophy.md` principle 4 calls that a future bug, and
 * ADR 0067's amendment section 4 names open-coded resolution as
 * precisely the shape of bug to design out.
 *
 * So both approval grains in the interface -- the per-size pill row
 * and the client-first accordion -- are assembled here, and both
 * bottom out in `approvalsForSize`. `clientApprovalSummary` does not
 * touch `size.approvals` itself; it calls `approvalsForSize` and
 * pivots the result. A test in
 * `__tests__/decorationFamilySelectors.spec.js` walks every
 * (size, client) cell and fails if the two grains ever disagree.
 *
 * What this module deliberately does NOT do
 * =========================================
 *
 * * **No capability claim.** ADR 0068 wants a declared vector / raster
 *   / stitch kind on the version; that column does not exist. The read
 *   model documents the gap at `decoration_family_view.py:370-385` and
 *   ships the digitization record instead. Deriving "can this version
 *   produce embroidery?" from digitization fields would be a guess
 *   rendered as a fact, so the version rail carries provenance and
 *   size counts and makes no capability claim.
 * * **No light/dark approval spanning.** `light_dark_pair_id` is a
 *   write-and-echo column; the approval-spanning resolver the 0067
 *   amendment requires does not exist. This module never widens an
 *   approval across a pair -- doing so would make a size read approved
 *   on the strength of a sibling, which is the exact widening the ADR
 *   forbids.
 *
 * DISPLAY ONLY -- pure functions over a fetched payload, no I/O.
 */

// ---------------------------------------------------------------------
// Approval vocabulary
// ---------------------------------------------------------------------

// ADR 0067 / Locked Decision 13: four values, and only four. These are
// the UI words. The backend speaks the schema's longer vocabulary
// (`awaiting_approval`, `changes_requested`); `approvalUiState` is the
// single translation point between the two.
export const APPROVAL_NOT_SENT = "not_sent";
export const APPROVAL_AWAITING = "awaiting";
export const APPROVAL_CHANGES = "changes";
export const APPROVAL_APPROVED = "approved";

const _STATUS_TO_UI = {
    approved: APPROVAL_APPROVED,
    changes_requested: APPROVAL_CHANGES,
    awaiting_approval: APPROVAL_AWAITING,
    not_sent: APPROVAL_NOT_SENT,
};

// Worst-first, mirroring `_APPROVAL_SEVERITY` in
// `decoration_family_view.py` and `APPR_SEVERITY` in the handoff's
// `dm-data.jsx`. A summary must never read better than reality.
const _SEVERITY_ORDER = [
    APPROVAL_CHANGES,
    APPROVAL_AWAITING,
    APPROVAL_NOT_SENT,
    APPROVAL_APPROVED,
];

// Human labels for the four states. One place, so a pill and an
// accordion chip cannot word the same state differently.
export const APPROVAL_LABELS = {
    [APPROVAL_NOT_SENT]: "Not sent",
    [APPROVAL_AWAITING]: "Awaiting client",
    [APPROVAL_CHANGES]: "Changes requested",
    [APPROVAL_APPROVED]: "Approved",
};

/**
 * Translate one backend approval status into the four-value UI
 * vocabulary.
 *
 * An unknown or missing status resolves to `not_sent`, never to
 * `approved` -- principle 5, fail toward the record. A status this
 * build does not recognise is a status nobody has confirmed, and
 * showing it as approval would clear a launch gate on a guess.
 *
 * @param {string|null|undefined} status
 * @returns {string} one of the four APPROVAL_* constants
 */
export function approvalUiState(status) {
    return _STATUS_TO_UI[status] ?? APPROVAL_NOT_SENT;
}

/**
 * Pick the worst outstanding state from a list of UI states.
 *
 * Returns `null` for an empty list -- "there is no question here",
 * which ADR 0067 is explicit is different from every negative state
 * and must not render as a warning (Locked Decision 16).
 *
 * @param {string[]} states
 * @returns {string|null}
 */
export function worstApprovalState(states) {
    if (!Array.isArray(states) || states.length === 0) return null;
    const present = new Set(states);
    return _SEVERITY_ORDER.find((s) => present.has(s)) ?? APPROVAL_NOT_SENT;
}

// ---------------------------------------------------------------------
// Payload navigation
// ---------------------------------------------------------------------

/**
 * Every size on the design, flattened across method families, in the
 * order the payload lists them.
 *
 * The families are a GROUPING and carry no state of their own (ADR
 * 0067), so flattening them loses nothing -- callers that need the
 * method heading iterate `payload.families` directly.
 *
 * @param {object|null} payload
 * @returns {object[]}
 */
export function designSizes(payload) {
    const families = payload?.families;
    if (!Array.isArray(families)) return [];
    return families.flatMap((family) =>
        Array.isArray(family?.sizes) ? family.sizes : []
    );
}

/**
 * Map of version id -> version record, for resolving the version an
 * approval was given against.
 *
 * @param {object|null} payload
 * @returns {Map<number, object>}
 */
function _versionsById(payload) {
    const map = new Map();
    const versions = payload?.versions;
    if (Array.isArray(versions)) {
        versions.forEach((v) => {
            if (v && v.id != null) map.set(Number(v.id), v);
        });
    }
    return map;
}

// ---------------------------------------------------------------------
// THE selector
// ---------------------------------------------------------------------

/**
 * The per-size approval readout: one entry per design client, carrying
 * THAT client's state FOR THIS SIZE.
 *
 * This is the single source both approval grains derive from. Nothing
 * in this module -- and nothing in the components -- may read
 * `size.approvals` directly; doing so re-opens the drift this function
 * exists to close.
 *
 * The service already back-fills a `not_sent` entry for every design
 * client missing from a size
 * (`decoration_family_view.py:394-405`). This function back-fills
 * again rather than trusting that, because the failure mode of a
 * missing entry is a client silently vanishing from a size nobody has
 * been shown -- which reads to an operator as "nothing outstanding
 * here."
 *
 * ADR 0070 -- "Same selector, filtered"
 * -------------------------------------
 * The design page is DUAL-MOUNTED: from the Library, where it answers
 * for every client, and from inside the store editor, where a store
 * has exactly one client (`stores.client_id`,
 * api/src/models/store.py:804) and the page must answer for that one.
 *
 * The store mount narrows THIS function rather than resolving
 * approvals its own way. `options.clientId` selects from the rows this
 * function already built -- it is a filter over the result, applied
 * last, and it changes no value. That is what makes the store mount a
 * view of the Library's answer instead of a second answer that agrees
 * today. ADR 0070's rejected "converge" option is exactly the shape a
 * parallel path would take.
 *
 * An ABSENT clientId means unfiltered, never "filter to nobody" --
 * principle 5, fail toward the record. The latter would empty the
 * Library's approvals silently, on a page that still looks calm.
 *
 * @param {object|null} payload the full family payload
 * @param {object|null} size one entry from `families[].sizes`
 * @param {{clientId?: number|string|null}} [options] ADR 0070 -- narrow
 *        the result to ONE client. Omitted/null/undefined = unfiltered.
 * @returns {Array<{clientId:number, clientName:string, state:string,
 *                  label:string, staleState:string|null,
 *                  versionId:number|null, versionNumber:number|null}>}
 */
export function approvalsForSize(payload, size, options = undefined) {
    const clients = Array.isArray(payload?.clients) ? payload.clients : [];
    if (clients.length === 0) return [];

    // Route params and JSON payloads carry ids as strings, so this
    // coerces rather than comparing with ===. A strict compare against
    // "7" would filter to nothing and render "No client yet" -- a calm,
    // wrong page.
    const wantRaw = options?.clientId;
    const wantClientId =
        wantRaw == null || wantRaw === "" ? null : Number(wantRaw);

    const versions = _versionsById(payload);
    const rows = Array.isArray(size?.approvals) ? size.approvals : [];
    const byClient = new Map();
    rows.forEach((row) => {
        if (row && row.client_id != null) {
            byClient.set(Number(row.client_id), row);
        }
    });

    const scoped =
        wantClientId == null
            ? clients
            : clients.filter((c) => Number(c.id) === wantClientId);

    return scoped.map((client) => {
        const clientId = Number(client.id);
        const row = byClient.get(clientId);
        const versionId =
            row?.artwork_version_id != null
                ? Number(row.artwork_version_id)
                : null;
        const version = versionId != null ? versions.get(versionId) : null;
        const state = approvalUiState(row?.status);
        return {
            clientId,
            clientName: client.name,
            state,
            label: APPROVAL_LABELS[state],
            staleState: row?.stale_state ?? null,
            // Locked Decision 15 -- an approval always states the
            // version it was given against. Null when nothing has been
            // sent, which is honest rather than defaulting to current.
            versionId,
            versionNumber: version?.version_number ?? null,
        };
    });
}

// ---------------------------------------------------------------------
// The second grain -- pivoted from the FIRST, never re-derived
// ---------------------------------------------------------------------

/**
 * The client-first approval accordion (ADR 0067 / Locked Decision 11):
 * one row per client with "N of M sizes approved" plus the worst
 * outstanding state and its count, expanding to a size-by-size table.
 *
 * Note what this does NOT do: it never reads `size.approvals`. It
 * calls `approvalsForSize` for each size and pivots the result, so the
 * accordion is a re-arrangement of exactly the values the pills show
 * rather than a second derivation of the same fact.
 *
 * @param {object|null} payload
 * @returns {Array<{clientId:number, clientName:string,
 *                  approvedCount:number, totalCount:number,
 *                  worstState:string|null, worstCount:number,
 *                  sizes:object[]}>}
 */
export function clientApprovalSummary(payload) {
    const clients = Array.isArray(payload?.clients) ? payload.clients : [];
    if (clients.length === 0) return [];

    const sizes = designSizes(payload);

    // One pass over the sizes through THE selector; everything below
    // is a pivot of `cells`, not a fresh read of the payload.
    const cells = sizes.map((size) => ({
        size,
        entries: approvalsForSize(payload, size),
    }));

    return clients.map((client) => {
        const clientId = Number(client.id);
        const perSize = cells.map(({ size, entries }) => {
            const entry = entries.find((e) => e.clientId === clientId);
            return {
                sizeId: size.id,
                widthIn: size.width_in,
                heightIn: size.height_in,
                substrateTarget: size.substrate_target,
                state: entry ? entry.state : APPROVAL_NOT_SENT,
                label: entry ? entry.label : APPROVAL_LABELS[APPROVAL_NOT_SENT],
                versionNumber: entry ? entry.versionNumber : null,
            };
        });

        const approvedCount = perSize.filter(
            (s) => s.state === APPROVAL_APPROVED
        ).length;
        // The worst OUTSTANDING state -- `approved` is not outstanding,
        // so a fully-approved client reports no worst state at all.
        const outstanding = perSize
            .map((s) => s.state)
            .filter((s) => s !== APPROVAL_APPROVED);
        const worstState = worstApprovalState(outstanding);
        const worstCount = worstState
            ? perSize.filter((s) => s.state === worstState).length
            : 0;

        return {
            clientId,
            clientName: client.name,
            approvedCount,
            totalCount: perSize.length,
            worstState,
            worstCount,
            sizes: perSize,
        };
    });
}

// ---------------------------------------------------------------------
// Readiness strip
// ---------------------------------------------------------------------

// Locked Decision 10: the strip LEADS with what needs the operator, in
// this order. Approved counts are never the lead, so `fully_approved`
// is deliberately absent from this list -- it renders as a quiet
// trailing total, not as a readiness item.
const _READINESS_BUCKETS = [
    {
        kind: "changes",
        rollupKey: "changes_requested",
        noun: "with changes requested",
        tone: "changes",
    },
    {
        kind: "awaiting",
        rollupKey: "awaiting",
        noun: "awaiting a client",
        tone: "awaiting",
    },
    {
        kind: "needs_production",
        rollupKey: "needs_production",
        noun: "without a production file",
        tone: "production",
    },
];

/**
 * The readiness strip's items, worst-first, zero-count buckets omitted.
 *
 * Counts come from `payload.rollup`, which the read model documents as
 * COUNTS OVER SIZES and never a design-level verdict -- a count cannot
 * be mistaken for "this design is approved" the way a status string
 * can.
 *
 * An empty return means nothing needs the operator; the caller renders
 * that as its own sentence rather than as three zeroes.
 *
 * @param {object|null} payload
 * @returns {Array<{kind:string, count:number, label:string, tone:string}>}
 */
export function readinessItems(payload) {
    const rollup = payload?.rollup;
    if (!rollup) return [];
    return _READINESS_BUCKETS.map((bucket) => {
        const count = Number(rollup[bucket.rollupKey] ?? 0);
        return {
            kind: bucket.kind,
            count,
            tone: bucket.tone,
            label: `${count} ${count === 1 ? "size" : "sizes"} ${bucket.noun}`,
        };
    }).filter((item) => item.count > 0);
}

export default {
    APPROVAL_NOT_SENT,
    APPROVAL_AWAITING,
    APPROVAL_CHANGES,
    APPROVAL_APPROVED,
    APPROVAL_LABELS,
    approvalUiState,
    worstApprovalState,
    designSizes,
    approvalsForSize,
    clientApprovalSummary,
    readinessItems,
};
